import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarPlot, ChartsAxisHighlight, ChartsLegend, ChartsXAxis, ChartsYAxis } from '@mui/x-charts';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { BoxWithHeader } from '../common';
import { DealerAccidentsOverviewProps } from './types';

const compactName = (name: string) => {
  const [first, ...rest] = name.split(' ');
  const last = rest.join(' ');
  if (!last) return first;
  return `${first} ${last.slice(0, 8)}${last.length > 8 ? '.' : ''}`;
};

export const AllDealersAccidentsBestTipers = ({ graphData, date }: DealerAccidentsOverviewProps) => {
  if (!graphData || !graphData.totals?.topTipsters?.values?.length) return null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const dealerLabels = graphData.totals.topTipsters.values.map((tipster) => compactName(tipster.name));
  const totalCounts = graphData.totals.topTipsters.values.map((tipster) => tipster.count);

  const layout = isMobile ? 'horizontal' : 'vertical';

  const xAxis = isMobile
    ? [
        {
          id: 'x-axis-id',
          scaleType: 'linear'
        } as const
      ]
    : [
        {
          id: 'x-axis-id',
          data: dealerLabels,
          scaleType: 'band'
        } as const
      ];

  const yAxis = isMobile
    ? [
        {
          id: 'y-axis-id',
          data: dealerLabels,
          scaleType: 'band'
        } as const
      ]
    : [
        {
          id: 'y-axis-id',
          scaleType: 'linear',
          tickMinStep: 1
        } as const
      ];

  const margin = isMobile
    ? { left: 70, right: 10, top: 20, bottom: 90 }
    : { left: 40, right: 20, top: 20, bottom: 130 };

  return (
    <BoxWithHeader
      title={
        <>
          <Typography variant="h6" fontWeight="bold">
            Přehled nejlepších tipařů napříč firmou
          </Typography>
          <Typography variant="body2" color="gray">
            {`Top 10 nejlepších tipařů (${date})`}
          </Typography>
        </>
      }
    >
      <Box sx={{ width: '100%', height: 600 }}>
        <ResponsiveChartContainer
          skipAnimation
          margin={margin}
          series={[
            {
              type: 'bar',
              data: totalCounts,
              label: 'Počet tipů',
              color: '#C62327',
              layout
            }
          ]}
          yAxis={yAxis}
          xAxis={xAxis}
        >
          <BarPlot borderRadius={2} barLabel="value" />
          <ChartsXAxis
            position="bottom"
            axisId="x-axis-id"
            label={isMobile ? 'Počet tipů' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 10
                  }
                : {
                    angle: -45,
                    textAnchor: 'end',
                    fontSize: 10
                  }
            }
          />
          <ChartsYAxis
            position="left"
            axisId="y-axis-id"
            label={!isMobile ? 'Počet tipů' : undefined}
            tickLabelStyle={
              isMobile
                ? {
                    fontSize: 9,
                    angle: -40
                  }
                : {
                    textAnchor: 'end',
                    fontSize: 9
                  }
            }
          />
          <ChartsAxisHighlight y="band" />
          <ChartsLegend position={{ vertical: 'bottom', horizontal: 'middle' }} />
        </ResponsiveChartContainer>
      </Box>
    </BoxWithHeader>
  );
};
